<template>
    <!-- The select renders immediately (empty options) while the events
         list loads in the background — no layout flash, loading is purely
         internal (per UX request). -->
    <BaseMultiselect
        :label="sga ? 'Evento SGA' : 'Evento LFP'"
        :name="sga ? 'sga_id' : 'lfp_id'"
        :value="selected?.id ?? null"
        :options="events || []"
        :label-key="sga ? 'name' : 'nombre'"
        value-key="id"
        :rules="!evento.experiencia ? 'required' : ''"
        @input="onSelect"
    />
</template>

<script setup>
    import { ref, watch, onMounted } from 'vue';
    import store from '@/store';
    import BaseMultiselect from '@/modules/admin/ui/BaseMultiselect.vue';

    const props = defineProps({
        evento: { type: Object, required: true },
        sga: { type: Boolean, default: false }
    });

    const emit = defineEmits(['selected', 'deselected']);

    const events = ref(null);
    const selected = ref(null);

    onMounted(() => {
        if (props.sga) {
            fetchSgaEvents();
        } else {
            fetchLfpEvents();
        }
    });

    // Keep the visual selection in sync with the form state. The `selected`
    // ref is only set during the initial fetch; without these watchers an
    // EXTERNAL id change (e.g. activating Experiencia clears lfp_id/sga_id
    // through the useEventFormWatchers) would clear the state while the
    // select kept painting the stale option. SGA mode self-heals because its
    // container unmounts on sga_id=null, but LFP stays mounted, so this
    // sync is what makes both behave identically.
    watch(() => props.evento.lfp_id, id => {
        selected.value = id
            ? (events.value || []).find(el => el.id === id) || null
            : null;
    });

    watch(() => props.evento.sga_id, id => {
        selected.value = id
            ? (events.value || []).find(el => el.id === id) || null
            : null;
    });

    onMounted(() => {
        if (props.sga) {
            fetchSgaEvents();
        } else {
            fetchLfpEvents();
        }
    });

    function fetchLfpEvents() {
        const alias = store.getters.getConfig?.getVuexAlias?.('back.events.lfp.index') || 'BACK_EVENTS_LFP_INDEX';
        store.dispatch(alias).then(res => {
            events.value = res.data;
            if (props.evento.lfp_id) {
                selected.value = events.value.find(el => el.id === props.evento.lfp_id);
            }
        });
    }

    function fetchSgaEvents() {
        const alias = store.getters.getConfig?.getVuexAlias?.('back.sga.events') || 'BACK_SGA_EVENTS';
        store.dispatch(alias).then(res => {
            events.value = res.data.data;
            if (props.evento.sga_id) {
                selected.value = events.value.find(el => el.id === props.evento.sga_id);
            }
        });
    }

    function onSelect(val) {
        const eventObj = (events.value || []).find(el => el.id === val);
        if (eventObj) {
            emit('selected', eventObj);
        } else if (val === null) {
            // Deselect: vue-multiselect emits null when the user removes the
            // current option. Notify the parent so it can clear sga_id/lfp_id
            // and any dependent state (products, nominativas, etc.).
            emit('deselected');
        }
    }
</script>
